/**
 * PublicProfileEditor — coach-only widget to opt in/out of public coach
 * profile + edit bio, slug, country, social links.
 *
 * Rendered inside `/profile` for coaches. Calls /api/coach/me + PATCH /api/coach/me.
 * Fully translated EN/ES/FR via LangContext.
 */
import React from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Globe, Sparkles, Copy, ExternalLink } from 'lucide-react';
import { useLang } from '@/context/LangContext';

export default function PublicProfileEditor() {
  const { t } = useLang();
  const [data, setData] = React.useState(null);
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api.get('/coach/me').then((r) => {
      setData(r.data);
      setForm({
        bio: r.data.bio || '',
        country: r.data.country || '',
        public_slug: r.data.public_slug || '',
        public_profile_enabled: !!r.data.public_profile_enabled,
        twitter: r.data.twitter || '',
        instagram: r.data.instagram || '',
        website: r.data.website || '',
      });
    }).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch('/coach/me', form);
      toast.success(t('ppe_save_success'));
      setData((d) => ({ ...d, public_slug: r.data.public_slug, public_profile_enabled: r.data.public_profile_enabled }));
      if (r.data.public_slug) update('public_slug', r.data.public_slug);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('ppe_save_error'));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;

  const publicUrl = data.public_slug ? `/coach/${data.public_slug}` : null;
  const fullUrl = publicUrl ? `${window.location.origin}${publicUrl}` : '';

  return (
    <div className="mt-8 bg-card-steel border border-gold/30 rounded-lg p-5" data-testid="public-profile-editor">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h2 className="font-h uppercase text-lg text-gold flex items-center gap-2">
            <Globe className="w-5 h-5" /> {t('ppe_title')}
          </h2>
          <p className="text-xs text-gray-400 mt-1 max-w-xl">{t('ppe_subtitle')}</p>
        </div>
        <label className="inline-flex items-center gap-2 shrink-0">
          <input
            type="checkbox"
            data-testid="public-profile-toggle"
            checked={!!form.public_profile_enabled}
            onChange={(e) => update('public_profile_enabled', e.target.checked)}
            className="w-4 h-4 accent-gold"
          />
          <span className="text-xs font-mono uppercase tracking-widest text-slate-300">
            {form.public_profile_enabled ? t('ppe_public') : t('ppe_private')}
          </span>
        </label>
      </div>

      {publicUrl && data.public_profile_enabled && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-xs">
          <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
          <span className="text-emerald-200 font-mono">{t('ppe_your_url')}</span>
          <code className="text-emerald-100">{fullUrl}</code>
          <button
            onClick={() => { navigator.clipboard.writeText(fullUrl); toast.success(t('ppe_copied')); }}
            className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-100"
            data-testid="copy-public-url"
          >
            <Copy className="w-3 h-3" /> {t('ppe_copy')}
          </button>
          <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-100 ml-auto">
            <ExternalLink className="w-3 h-3" /> {t('ppe_open')}
          </a>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{t('ppe_slug')}</label>
          <input
            data-testid="public-profile-slug"
            value={form.public_slug}
            onChange={(e) => update('public_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="coach-rivera"
            className="mt-1 w-full bg-navy-deep border border-gold/30 rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{t('ppe_country')}</label>
          <input
            data-testid="public-profile-country"
            value={form.country}
            onChange={(e) => update('country', e.target.value)}
            placeholder={t('ppe_country_placeholder')}
            className="mt-1 w-full bg-navy-deep border border-gold/30 rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{t('ppe_bio')}</label>
          <textarea
            data-testid="public-profile-bio"
            value={form.bio}
            onChange={(e) => update('bio', e.target.value.slice(0, 600))}
            rows={3}
            placeholder={t('ppe_bio_placeholder')}
            className="mt-1 w-full bg-navy-deep border border-gold/30 rounded-md px-3 py-2 text-sm"
          />
          <div className="text-[10px] text-slate-500 text-right mt-0.5">{(form.bio || '').length}/600</div>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{t('ppe_twitter')}</label>
          <input
            data-testid="public-profile-twitter"
            value={form.twitter}
            onChange={(e) => update('twitter', e.target.value.replace('@', ''))}
            placeholder="coachrivera"
            className="mt-1 w-full bg-navy-deep border border-gold/30 rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{t('ppe_instagram')}</label>
          <input
            data-testid="public-profile-instagram"
            value={form.instagram}
            onChange={(e) => update('instagram', e.target.value.replace('@', ''))}
            placeholder="coachrivera"
            className="mt-1 w-full bg-navy-deep border border-gold/30 rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{t('ppe_website')}</label>
          <input
            data-testid="public-profile-website"
            value={form.website}
            onChange={(e) => update('website', e.target.value)}
            placeholder={t('ppe_website_placeholder')}
            className="mt-1 w-full bg-navy-deep border border-gold/30 rounded-md px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div className="mt-5">
        <button
          onClick={save}
          disabled={saving}
          data-testid="public-profile-save"
          className="px-4 py-2 rounded-md bg-gold text-navy-deep font-h uppercase tracking-wider text-sm hover:bg-gold/90 disabled:opacity-50"
        >
          {saving ? t('ppe_saving') : t('ppe_save')}
        </button>
      </div>
    </div>
  );
}
